// src/components/PINPrompt.js
import React, { useState, useEffect, useRef } from "react";
import { verifyPin } from "../utils/PINService";
import { useHistory } from "react-router-dom";
import { setPinConfirmedNow } from "../utils/PINSession";
import { isZh } from "../utils/language";

/**
 * Centered PIN prompt:
 * - middle-of-page layout (vertical + horizontal)
 * - input narrow for 6 digits (uses ch unit so it's sized for characters)
 * - larger font for readability
 * - buttons & label centered
 * - attempts count on the right of buttons area
 */

const showMessage = (history, message, level = "error") => {
  if (typeof window !== "undefined") {
    if (typeof window.appToast === "function") {
      window.appToast(message, { type: level });
      return;
    }
    if (typeof window.toast === "function") {
      window.toast(message, { type: level });
      return;
    }
  }
  try {
    history.replace("/", { flash: message, flashLevel: level });
    return;
  } catch (e) {
    // fallback
    // eslint-disable-next-line no-alert
    alert(message);
  }
};

const PINPrompt = ({ onSuccess, onCancel }) => {
  const [pin, setPin] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const history = useHistory();

  // mounted ref to avoid setting state after unmount
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (loading) return;

    if (!/^\d{6}$/.test(pin)) {
      setErrorMsg(isZh() ? "PIN 必須為 6 位數字。" : "PIN harus 6 digit angka.");
      return;
    }

    if (mountedRef.current) setLoading(true);
    try {
      const res = await verifyPin(pin);
      const ok =
        !!(res &&
          (res.ok === true ||
            res.ok === "true" ||
            res.status === "ok" ||
            (typeof res.message === "string" && res.message.toLowerCase().includes("valid"))));

      if (ok) {
        setPinConfirmedNow();
        if (typeof onSuccess === "function") return onSuccess();
        history.goBack();
        return;
      }

      // wrong PIN -> decrement attempts safely using functional update
      if (!mountedRef.current) return;
      setAttemptsLeft((prev) => {
        const remaining = prev - 1;
        if (remaining > 0) {
          //showMessage(history, ( isZh() ? `PIN 錯誤。您還有 ${remaining} 次嘗試機會。` : `PIN salah. Anda memiliki ${remaining} kesempatan tersisa.`), "error");
          setErrorMsg(isZh() 
            ? `PIN 錯誤。您還有 ${remaining} 次機會。`
            : `PIN salah. Sisa ${remaining} kesempatan.`
          );
        } else {
          setErrorMsg(isZh() 
            ? "PIN 錯誤 3 次，將返回首頁"
            : "PIN salah 3x, kembali ke halaman utama"
          );

          setTimeout(() => {
            history.replace("/");
          }, 1500); // kasih waktu user baca
          //showMessage(history, ( isZh() ? "PIN 錯誤 3 次。將為您跳轉至首頁。" : "PIN salah 3x. Anda dialihkan ke halaman utama."), "error");
          //history.replace("/");
        }
        return remaining;
      });
    } catch (err) {
      if (!mountedRef.current) return;
      setAttemptsLeft((prev) => {
        const remaining = prev - 1;
        if (remaining > 0) {
          //showMessage(history, ( isZh() ? `PIN 驗證失敗。請再試一次。剩餘次數：${remaining}` : `Gagal memverifikasi PIN. Coba lagi. Kesempatan tersisa: ${remaining}`), "error");
          setErrorMsg(isZh() 
            ? `PIN 錯誤。您還有 ${remaining} 次機會。`
            : `PIN salah. Sisa ${remaining} kesempatan.`
          );
        } else {
          setErrorMsg(isZh() 
            ? "PIN 錯誤 3 次，將返回首頁"
            : "PIN salah 3x, kembali ke halaman utama"
          );

          setTimeout(() => {
            history.replace("/");
          }, 1500); // kasih waktu user baca
          // showMessage(history, ( isZh() ? "PIN 驗證失敗（3 次）。將跳轉至首頁。" : "Gagal memverifikasi PIN (3 kali). Dialihkan ke halaman utama."), "error");
          //history.replace("/");
        }
        return remaining;
      });
      // eslint-disable-next-line no-console
      console.error("verifyPin error", err);
    } finally {
      // only update local state if still mounted
      if (mountedRef.current) {
        setLoading(false);
        setPin("");
      }
    }
  }

  return (
    <div
      style={{
        minHeight: "60vh", // pushes content to vertical center
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        boxSizing: "border-box",
      }}
    >
      <div
        role="dialog"
        aria-labelledby="pin-title"
        style={{
          width: "100%",
          maxWidth: "28rem",
          padding: "1.25rem 1.5rem",
          boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
          borderRadius: 10,
          background: "#fff",
          textAlign: "center",
        }}
      >
        <h3 id="pin-title" style={{ fontSize: "1.5rem", margin: "0 0 0.75rem 0" }}>
          { isZh() ? "確認 PIN" : "Konfirmasi PIN"}
        </h3>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem" }}>
          <input
            aria-label="PIN"
            autoFocus
            type="password"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={pin}
            onChange={(e) => {
                setPin(e.target.value.replace(/\D/g, "").slice(0, 6));
                setErrorMsg(""); // 🔥 reset error setiap user mulai ketik lagi
              }}
            placeholder={ isZh() ? "請輸入 PIN（6 位數字）" : "Masukkan PIN (6 digit)"}
            style={{
              fontSize: "1.5rem",           // larger font for readability
              padding: "0.6rem 0.5rem",
              width: "12ch",               // narrow width appropriate for 6 digits
              textAlign: "center",
              borderRadius: 8,
              border: "1.5px solid #222",
              outline: "none",
            }}
          />

          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", width: "100%", justifyContent: "center" }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                fontSize: "1.125rem",
                padding: "0.5rem 1rem",
                borderRadius: 10,
                cursor: loading ? "not-allowed" : "pointer",
                minWidth: "8rem",
                border: "1px solid #222",
                background: "#fff",
              }}
            >
              {loading ? ( isZh() ? "驗證中…" : "Memeriksa...") : ( isZh() ? "確認" : "Konfirmasi")}
            </button>

            <button
              type="button"
              onClick={() => {
                if (onCancel) onCancel();
                else history.replace("/");
              }}
              style={{
                fontSize: "1.125rem",
                padding: "0.5rem 1rem",
                borderRadius: 10,
                minWidth: "6rem",
                border: "1px solid #222",
                background: "#fff",
              }}
            >
              { isZh() ? "取消" : "Batal"}
            </button>
            <div style={{ marginLeft: "0.75rem", fontSize: "1rem", color: "#666", display: "none" }}>
              { isZh() ? "剩餘嘗試次數：" : "Sisa percobaan:"} <strong style={{ fontSize: "1.05rem" }}>{attemptsLeft}</strong>
            </div>
          </div>
        </form>

        {errorMsg && (
          <div style={{ 
            color: "red", 
            marginTop: "0.5rem",
            fontSize: "0.95rem"
          }}>
            {errorMsg}
          </div>
        )}
      </div>
    </div>
  );
};

export default PINPrompt;
